import {FC, useState} from "react";
import {AdminTable, AdminTableProps} from "@/components/common/table";
import {GRID_CHECKBOX_SELECTION_COL_DEF, GridSelectionModel} from "@mui/x-data-grid-pro";
import {ICompletedLessonDto} from "@/types/admin";
import {useTeamProgressContext} from "@/contexts/TeamProgressContext";
import {IBaseSearchDTO} from "@/components/common/table/AdminTable";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import dayjs from "dayjs";
import {TeamProgressSection, TeamProgressWidget} from "@/enums";
import {convertSecondsToReadable} from "@/utils";
import {useManageTeamProgressReport} from "../../hooks/useDownloadReport";
import {useExportTeamProgressContext} from "@/contexts/ExportTeamProgressContext";
import {formatDateCell} from "@/utils/date-formatters";
import {useNavigate} from "react-router-dom";
import {getDateTo} from "../../utils/common-utils";

interface ICompletedLessonSearchDTO extends IBaseSearchDTO {
	archivedUsers?: boolean;
	dateFrom?: string;
	dateTo?: string;
}

const CompletedLessonsDataGrid: FC = () => {
	const [selected, setSelected] = useState<GridSelectionModel>([]);
	const {filters, updateSectionState} = useTeamProgressContext();
	const {exportTaskExists} = useExportTeamProgressContext();
	const {handleUpdatedFilter, handleUpdatedSort, initDownload} = useManageTeamProgressReport(TeamProgressWidget.completedLessons);
	const {t} = useSSTranslation();
	const isExporting = exportTaskExists(TeamProgressWidget.completedLessons);
	const navigate = useNavigate();

	const getHeadCells = (): AdminTableProps<"user", ICompletedLessonDto, ICompletedLessonSearchDTO>["columns"] => ([
		{
			...GRID_CHECKBOX_SELECTION_COL_DEF,
			type: "string",
			hideable: false
		},
		{
			field: "firstname",
			flex: 1,
			headerName: t("completed_lessons_column_firstname", "First Name"),
			type: "string"
		},
		{
			field: "lastname",
			flex: 1,
			headerName: t("completed_lessons_column_lastname", "Last Name"),
			type: "string"
		},
		{
			field: "lesson",
			flex: 1,
			headerName: t("completed_lessons_column_lesson", "Lesson"),
			type: "string"
		},
		{
			field: "duration",
			flex: 1,
			headerName: t("completed_lessons_column_duration", "Duration"),
			type: "number",
			valueGetter: (e) => e.value ? convertSecondsToReadable(e.value) : "",
			filterable: false
		},
		{
			field: "supervisor",
			flex: 1,
			type: "string",
			headerName: t("completed_lessons_column_supervisor", "Supervisor")
		},
		{
			field: "division",
			flex: 1,
			type: "string",
			headerName: t("completed_lessons_column_division", "Division")
		},
		{
			field: "jobCategory",
			flex: 1,
			headerName: t("completed_lessons_column_job_category", "Job Category"),
			type: "string"
		},
		{
			field: "completedOn",
			flex: 1,
			type: "date",
			headerName: t("completed_lessons_column_completed_on", "Completed on"),
			valueGetter: (e) => formatDateCell(e.value)
		},
		{
			field: "enrollmentType",
			flex: 1,
			headerName: t("completed_lessons_column_enrollment_type", "Enrollment Type"),
			type: "string",
			valueGetter: (params) =>
				params.value ? t(`user_enrollment_type_${params.value}`, params.value) : ""
		},
		{
			field: "course",
			flex: 1,
			headerName: t("completed_lessons_column_course", "Course"),
			type: "string"
		},
		{
			field: "email",
			flex: 1,
			headerName: t("completed_lessons_column_email", "Email"),
			type: "string"
		},
		{
			field: "phone",
			flex: 1,
			headerName: t("completed_lessons_column_phone", "Phone"),
			type: "string"
		},
		{
			field: "jobTitle",
			flex: 1,
			headerName: t("completed_lessons_column_job_title", "Job Title"),
			type: "string"
		},
		{
			field: "status",
			flex: 1,
			headerName: t("completed_lessons_column_status", "Status"),
			type: "string"
		},
		{
			field: "streak",
			flex: 1,
			headerName: t("completed_lessons_column_streak", "Streak"),
			type: "number"
		}
	]);

	const headCells = getHeadCells();

	// Create translated filter description
	const getFilterDescription = (): string => {
		const {dateRangeType, dateFrom, dateTo} = filters;

		switch (dateRangeType) {
			case "lastWeek":
				return t("completed_lessons_viewing_stats_past_week", "VIEWING STATS FROM THE PAST WEEK");
			case "lastMonth":
				return t("completed_lessons_viewing_stats_past_month", "VIEWING STATS FROM THE PAST MONTH");
			case "lastYear":
				return t("completed_lessons_viewing_stats_past_year", "VIEWING STATS FROM THE PAST YEAR");
			case "customDateRange": {
				const fromDate = dayjs(dateFrom).format("MM/DD/YYYY");
				const toDate = dayjs(dateTo).format("MM/DD/YYYY");
				return `${t("completed_lessons_viewing_stats_custom_range", "")} ${fromDate} - ${toDate}`;
			}
			default:
				return t("completed_lessons_viewing_stats_all_time", "VIEWING STATS FROM ALL TIME");
		}
	};

	const handleUpdateDatagridState = (isLoading: boolean) => {
		updateSectionState(isLoading, TeamProgressSection.dataGrid);
	}

	const filterDescription = getFilterDescription();
	const searchDTOverride: Partial<ICompletedLessonSearchDTO> = {
		archivedUsers: filters.archivedUsers,
		dateFrom: dayjs(filters.dateFrom).format("YYYY-MM-DD"),
		dateTo: getDateTo(filters.dateTo)
	};

	return (
		<AdminTable<"user", ICompletedLessonDto, ICompletedLessonSearchDTO>
			enableAdvancedStringFilters={true}
			url="/api/v1/team-progress/completed-lessons"
			type="user"
			onEditAction={e => navigate("/admin/users/" + e.userId.toString())}
			columns={headCells}
			disableColumnResize={false}
			selectionModel={selected}
			onSelectionModelChange={e => setSelected(e)}
			searchDTOverride={() => searchDTOverride}
			checkboxSelection={false}
			initialState={{
				columns: {
					columnVisibilityModel: {
						email: false,
						phone: false,
						jobTitle: false,
						streak: false,
						jobCategory: false,
						course: false,
						status: false
					}
				}
			}}
			toolBarText={filterDescription}
			exportToolTipText={t("data_grid_export_tooltip", "Coming Soon")}
			disableExport
			showAlternativeExportoption
			isExporting={isExporting}
			onClickAlternativeExportOption={initDownload}
			updatedFilterCallback={handleUpdatedFilter}
			updatedSortCallback={handleUpdatedSort}
			isLoadingCallback={handleUpdateDatagridState}
		/>
	);
}

export default CompletedLessonsDataGrid;
